import React, { useEffect, useRef, useState } from "react";
import Dialog from "@components/Dialog";
import { observer } from "mobx-react-lite";
import { useLang, useLength, useSettings } from "@src/hooks/useLang";
import { Column, Row } from "@src/components/Flex";
import styled from "@emotion/styled";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import moment from "moment";
import { IDialogPropTypes } from "rc-dialog/es/IDialogPropTypes";
import DatePicker from "@components/DatePicker";
import Select from "@components/Select";
import { useDeviceCalibrationScreenVM } from "@screens/DeviceCalibrationScreen/DeviceCalibrationScreenVM";
import Input from "@components/Input";
import Button from "@components/Button";
import {
  TCalibration,
  TCreateCalibrationParams,
  TEditCalibrationParams,
} from "@services/calibrationService";
import { toast } from "react-toastify";
import Loading from "@components/Loading";
import { useStores } from "@stores/useStores";
import { LengthUnit, WeightUnit } from "@services/demosService";
import {
  convertLength,
  convertWeight,
  roundLengthForDisplay,
  roundLengthForStorage,
} from "@src/utils/convert";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import {
  calibrationPlaceLabel,
  calibrationWidthMeters,
  cropIdToSave,
  initialCropId,
} from "@src/utils/calibrationDisplay";

function toLocalDatetimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const Root = styled(Dialog)`
  margin: 0;
  .rc-dialog-body {
    padding: 0;
  }
`;
interface IProps extends IDialogPropTypes {
  id?: string;
  // A new calibration always carries crop and width; an edit may leave them
  // out to keep what is stored (see TEditCalibrationParams).
  onSubmit?: (
    params: (TCreateCalibrationParams | TEditCalibrationParams) & { id?: string }
  ) => Promise<void>;
  onClose?: () => void;
}

const Label = styled.label`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #899093;
  margin-bottom: 6px;
`;

/**
 * Two panes side by side on desktop; stacked on a phone.
 *
 * The dialog itself is already clamped to the viewport width in
 * components/Dialog/styles.css, but at 375px two `flex: 1` panes still split
 * that into a pair of ~170px columns — the datepicker and the weight/unit row
 * both overflow theirs. Stacking gives each its full width and lets the dialog
 * scroll, since the two panes together are taller than a phone screen.
 */
const DialogBody = styled.div`
  display: flex;
  max-height: calc(100vh - 48px);
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    max-height: calc(100vh - 96px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  padding: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
    align-items: stretch;
    /* Beats the width:fit-content that Flex's Column hardcodes, so the
       start/stop fields fill the pane once it is the full screen width. */
    > * {
      width: 100%;
    }
  }
`;

const LeftPane = styled(Pane)`
  border-right: 1px solid #cbd5d8;
  height: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-right: none;
    border-bottom: 1px solid #cbd5d8;
    height: auto;
  }
`;

const RightPane = styled(Pane)`
  max-height: calc(100vh - 48px);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-height: none;
  }
`;

/* Aligns the form with the title in the left pane. Once stacked there is no
   title to align to, and the gap just pushes the fields below the fold. */
const RightPaneSpacer = styled.div`
  height: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

/* The calendar is scaled up to be comfortable on a mouse; at 1.2x it is wider
   than a 375px viewport, so it renders at its natural size on a phone. */
const DatePickerRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  transform: scale(1.2);
  margin-bottom: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    transform: none;
    margin-bottom: 16px;
  }
`;

/* Save/Cancel side by side, right-aligned. On a phone they go full width and
   stack, with Save first so the primary action sits under the thumb. */
const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column-reverse;
    gap: 12px;
    button {
      width: 100%;
    }
  }
`;

const FormActions = styled.div`
  width: 100%;
  margin-top: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-top: 24px;
  }
`;

const StyledInput = styled.input`
  height: 40px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    border-color: #68ad18;
  }
`;

// Defaults are clamped to "now" — a 9:00–12:00 window prefilled at 11:00 would
// otherwise end in the future, which the backend rejects.
const clampToNow = (m: moment.Moment) => moment.min(m, moment());
// Mirrors the backend cap. A calibration is one weighed load — a single pass,
// hours at most. A wider window is a mistyped date, and it makes the backend
// read every track row in the range (a device reports every ~30-70s).
const MAX_WINDOW_HOURS = 24;
const defaultStartTime = () =>
  toLocalDatetimeString(clampToNow(moment().startOf("day").add(9, "hours")).toDate());
const defaultEndTime = () =>
  toLocalDatetimeString(clampToNow(moment().startOf("day").add(12, "hours")).toDate());

/**
 * Width the form starts with: the calibration's own, else the device's. The
 * backend lists "no width entered" (driver-app runs) as 0, which counts as
 * none; `fromCalibration` records which one it was.
 */
const widthPrefill = (
  calibration: TCalibration | undefined,
  deviceWidth: number | undefined,
  lengthUnit: LengthUnit
) => {
  const ownMeters = calibration != null ? calibrationWidthMeters(calibration) : null;
  const meters = ownMeters ?? deviceWidth ?? 0;
  return {
    display: roundLengthForDisplay(convertLength(meters, LengthUnit.Meter, lengthUnit)),
    meters,
    fromCalibration: ownMeters != null,
  };
};

const DeviceCalibrationDialog: React.FC<IProps> = ({ onClose, ...rest }) => {
  const lang = useLang();
  const vm = useDeviceCalibrationScreenVM();
  const { settingsStore } = useStores();
  const locLength = useLength();
  const { length: lengthUnit } = useSettings();
  const calibration = vm.calibrations.find(
    (c) => c.id === vm.editCalibrationId
  );

  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [cropId, setCropId] = useState<string>("");
  const [weight, setWeight] = useState<number>(0);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(settingsStore.weightUnit);
  // Shown and typed in the user's length unit; the API always takes metres.
  // `prefilledWidth` lets an untouched field submit the stored metres value
  // verbatim instead of a m -> ft -> m round-trip (6.7 -> 21.98 -> 6.6995).
  const [width, setWidth] = useState<number>(0);
  // `fromCalibration` is false when the prefill is the device's width because
  // the calibration has none of its own (driver-app runs): an untouched field
  // is then left out of the edit, so the machine's width timeline still applies.
  const prefilledWidth = useRef<{
    display: number;
    meters: number;
    fromCalibration: boolean;
  } | null>(null);
  const [startTime, setStartTime] = useState<string>(defaultStartTime());
  const [endTime, setEndTime] = useState<string>(defaultEndTime());
  const nowLocal = toLocalDatetimeString(new Date());
  // Bounds shown on the pickers; handleSubmit re-checks, since a typed-in value
  // can still fall outside min/max in some browsers. A half-typed field parses
  // as invalid — leave that side unbounded rather than emit a NaN attribute.
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const maxEndLocal = startMoment.isValid()
    ? toLocalDatetimeString(
        clampToNow(startMoment.clone().add(MAX_WINDOW_HOURS, "hours")).toDate()
      )
    : nowLocal;
  const minStartLocal = endMoment.isValid()
    ? toLocalDatetimeString(
        endMoment.clone().subtract(MAX_WINDOW_HOURS, "hours").toDate()
      )
    : undefined;

  const resetAndClose = () => {
    setWeight(0);
    setWidth(0);
    prefilledWidth.current = null;
    setWeightUnit(settingsStore.weightUnit);
    setStartTime(defaultStartTime());
    setEndTime(defaultEndTime());
    setDate(new Date());
    onClose && onClose();
  };

  const handleSubmit = () => {
    const start = moment(startTime);
    const end = moment(endTime);
    if (end.isSameOrBefore(start)) {
      toast.warning("End time must be after start time");
      return;
    }
    // The window must be fully harvested already — a future end time yields a
    // coefficient computed from a partial pass. Backend rejects it too.
    if (end.isAfter(moment())) {
      toast.warning(
        lang.devices.calibrationFutureTimeError ??
          "Calibration time cannot be in the future"
      );
      return;
    }
    if (end.diff(start, "hours", true) > MAX_WINDOW_HOURS) {
      toast.warning(
        lang.devices.calibrationWindowTooWideError ??
          "Calibration window cannot be longer than 24 hours"
      );
      return;
    }
    // An edit may keep a calibration without a crop (driver-app runs); a new
    // one needs a listed crop.
    const cultureId = cropIdToSave(
      cropId,
      vm.crops.map(({ id }) => id),
      calibration != null
    );
    if (width == null || weight == null || rest.onSubmit == null || cultureId === null) {
      toast.warning("You need to fill in all the data");
      return;
    }
    setLoading(true);

    const weightInKg = convertWeight(weight, weightUnit, WeightUnit.Kilogram, {
      poundsPerBushel: settingsStore.poundsPerBushel,
      poundsPerBag: settingsStore.poundsPerBag,
    });

    const prefilled = prefilledWidth.current;
    const widthUntouched = prefilled != null && width === prefilled.display;
    const widthInMeters = widthUntouched
      ? prefilled.meters
      : roundLengthForStorage(
          convertLength(width, lengthUnit, LengthUnit.Meter)
        );
    const keepStoredWidth =
      calibration != null && widthUntouched && !prefilled.fromCalibration;

    const params: (TCreateCalibrationParams | TEditCalibrationParams) & { id?: string } = {
      id: calibration?.id,
      culture_id: cultureId,
      start: start.toISOString(),
      stop: end.toISOString(),
      reaper_width: keepStoredWidth ? undefined : { value: widthInMeters, unit: "m" },
      weight: { value: weightInKg, unit: "kg" },
    };
    rest
      .onSubmit(params)
      .then(resetAndClose)
      .catch((e) => toast.error(e.message ?? e.toString()))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const startDate = new Date(startTime);
    if (!isNaN(startDate.getTime()) && startDate.toDateString() !== date.toDateString()) {
      setDate(startDate);
    }
  }, [startTime, date]);

  const deviceWidth = vm.device?.harvester.default_reaper.width;

  useEffect(() => {
    if (width === 0) {
      const prefill = widthPrefill(calibration, deviceWidth, lengthUnit);
      prefilledWidth.current = prefill;
      setWidth(prefill.display);
    }
    if (weight === 0) {
      const calibrationWeight = calibration?.weight.value ?? 0;
      if (calibrationWeight > 0) {
        const convertedWeight = convertWeight(calibrationWeight, WeightUnit.Kilogram, weightUnit, {
          poundsPerBushel: settingsStore.poundsPerBushel,
          poundsPerBag: settingsStore.poundsPerBag,
        });
        setWeight(convertedWeight);
      }
    }
  }, [
    width,
    deviceWidth,
    calibration,
    weight,
    weightUnit,
    lengthUnit,
    settingsStore.poundsPerBushel,
    settingsStore.poundsPerBag,
  ]);

  // Crop, width and weight are set afresh on every opening. This component
  // stays mounted between openings and the effect above only fills a field
  // while it is empty (0 / ""), so the next row opened used to show the
  // previous row's crop and weight — and the device's width instead of its own
  // — and saving wrote them onto it. (resetAndClose runs after the save's
  // promise, unbatched in React 17, so the effect above refills from the row
  // just saved before the dialog closes.)
  //
  // Only a listed crop is preselected: a driver-app calibration stores none,
  // or a field the server detected.
  useEffect(() => {
    if (!vm.dialogVisible) return;
    setCropId(
      initialCropId(
        calibration?.culture?.id,
        vm.crops.map(({ id }) => id)
      )
    );
  }, [vm.dialogVisible, calibration, vm.crops]);

  useEffect(() => {
    if (!vm.dialogVisible) return;
    const prefill = widthPrefill(calibration, deviceWidth, lengthUnit);
    prefilledWidth.current = prefill;
    setWidth(prefill.display);
  }, [vm.dialogVisible, calibration, deviceWidth, lengthUnit]);

  useEffect(() => {
    if (!vm.dialogVisible) return;
    const unit = settingsStore.weightUnit;
    const kg = calibration?.weight.value ?? 0;
    setWeightUnit(unit);
    setWeight(
      kg > 0
        ? convertWeight(kg, WeightUnit.Kilogram, unit, {
            poundsPerBushel: settingsStore.poundsPerBushel,
            poundsPerBag: settingsStore.poundsPerBag,
          })
        : 0
    );
  }, [
    vm.dialogVisible,
    calibration,
    settingsStore.weightUnit,
    settingsStore.poundsPerBushel,
    settingsStore.poundsPerBag,
  ]);

  // The window is prefilled only when the calibration being edited (or the
  // tracks "Use for calibration" range) changes. It used to live in the effect
  // above, whose deps include weight/crop/width, so typing a weight after
  // moving the start or end time silently put the stored times back.
  useEffect(() => {
    const source =
      calibration != null
        ? { start: calibration.start, stop: calibration.stop }
        : vm.prefillFromTracks;
    if (source == null) return;
    const start = moment(source.start);
    const end = moment(source.stop);
    setDate(start.toDate());
    setStartTime(toLocalDatetimeString(start.toDate()));
    setEndTime(toLocalDatetimeString(end.toDate()));
  }, [calibration, vm.prefillFromTracks]);

  return (
    <Root
      destroyOnClose
      style={{ width: 649 }}
      closeIcon={<div />}
      onClose={resetAndClose}
      {...rest}
    >
      {vm.dialogVisible ? (
        <DialogBody>
          <LeftPane>
            <Text size={18} type="heading" color="#494C5B">
              {calibration
                ? lang.devices.editCalibrationTitle
                : lang.devices.calibrationTitle}
            </Text>
            <SizedBox height={4} />
            <Text size={15} color="#899093">
              {lang.devices.calibrationSubtitle}
            </Text>
            <SizedBox height={24} />
            <DatePickerRow>
              <DatePicker
                selected={date}
                maxDate={new Date()}
                onChange={(newDate) => {
                  if (newDate) {
                    setDate(newDate);
                    const pad = (n: number) => String(n).padStart(2, "0");
                    const dateStr = `${newDate.getFullYear()}-${pad(newDate.getMonth() + 1)}-${pad(newDate.getDate())}`;
                    const startParts = startTime.split("T");
                    const endParts = endTime.split("T");
                    setStartTime(startParts[1] ? `${dateStr}T${startParts[1]}` : startTime);
                    setEndTime(endParts[1] ? `${dateStr}T${endParts[1]}` : endTime);
                  }
                }}
              />
            </DatePickerRow>
            <Column style={{ gap: 12 }}>
              <Column style={{ gap: 4 }}>
                <Label>{lang.analytics?.startTime ?? "Start time"}</Label>
                <StyledInput
                  type="datetime-local"
                  min={minStartLocal}
                  max={nowLocal}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </Column>
              <Column style={{ gap: 4 }}>
                <Label>{lang.analytics?.endTime ?? "End time"}</Label>
                <StyledInput
                  type="datetime-local"
                  min={startTime}
                  max={maxEndLocal}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </Column>
            </Column>
          </LeftPane>
          <RightPane>
            <RightPaneSpacer />
            <Select
              label={lang.fields.newFieldCrop}
              value={cropId}
              onChange={setCropId}
              options={vm.crops.map((crop) => ({
                title: crop.name,
                value: crop.id,
              }))}
              // Editing a calibration with no listed crop (a driver-app run):
              // show what is stored ("—" for nothing) until one is picked.
              renderValue={(option) =>
                option?.title ??
                (calibration != null ? (
                  <span style={{ color: "#899093" }}>
                    {calibrationPlaceLabel(calibration)}
                  </span>
                ) : undefined)
              }
            />
            <SizedBox height={16} />
            <Row style={{ gap: 8 }}>
              <Input
                label={lang.devices.calibrationWeightTitle}
                value={weight}
                onChange={(e) =>
                  !isNaN(Number(e.target.value)) &&
                  setWeight(Number(e.target.value))
                }
                style={{ flex: 1 }}
              />
              <div style={{ marginTop: 22, minWidth: 80 }}>
                <Select
                  value={weightUnit}
                  onChange={(value) => setWeightUnit(value as WeightUnit)}
                  renderValue={(option) => option?.value}
                  options={[
                    { title: lang.settings.ton, value: WeightUnit.Ton },
                    { title: lang.settings.centner, value: WeightUnit.Centner },
                    { title: lang.settings.kilograms, value: WeightUnit.Kilogram },
                    { title: lang.settings.pounds, value: WeightUnit.Pound },
                    { title: lang.settings.shortTon, value: WeightUnit.ShortTon },
                    { title: lang.settings.bushels, value: WeightUnit.Bushel },
                    { title: lang.settings.bag, value: WeightUnit.Bag },
                  ]}
                />
              </div>
            </Row>
            <SizedBox height={16} />
            <Input
              label={`${lang.devices.calibrationHeaderTitle} (${locLength()})`}
              value={width}
              onChange={(e) =>
                !isNaN(Number(e.target.value)) &&
                setWidth(Number(e.target.value))
              }
            />
            <FormActions>
              <ButtonRow>
                <Button
                  onClick={resetAndClose}
                  style={{ width: "fit-content" }}
                  kind="outline"
                >
                  {lang.devices.calibrationCancelButton}
                </Button>
                <Button
                  style={{ width: "fit-content", minWidth: 100 }}
                  onClick={!loading ? handleSubmit : undefined}
                >
                  {loading ? <Loading /> : lang.devices.calibrationSaveButton}
                </Button>
              </ButtonRow>
            </FormActions>
          </RightPane>
        </DialogBody>
      ) : null}
    </Root>
  );
};
export default observer(DeviceCalibrationDialog);
